import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Input, Typography, Space, Alert, Spin } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, CheckOutlined } from '@ant-design/icons';
import './Auth/auth.css';
import { API_BASE } from './config/api';
import useCurrencyDisplay from './utils/useCurrencyDisplay';

const { Title, Text } = Typography;

const Signup = () => {
  const { formatLocal } = useCurrencyDisplay();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [step, setStep] = useState(1); // 1: select plan, 2: create account
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [error, setError] = useState('');
  const [isFreeTrial, setIsFreeTrial] = useState(false);

  // Fetch subscription plans and check for pre-selected plan
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch(`${API_BASE}/public/subscription-plans`);
        const data = await response.json();
        if (response.ok) {
          // Only show paid plans
          const paidPlans = (data.items || []).filter((plan) => {
            const price = Number(plan.monthly_price ?? plan.monthlyPrice ?? 0);
            return String(plan.plan_type || '').toLowerCase() !== 'free' && price > 0;
          });
          setPlans(paidPlans);

          // Check if user came from landing page with a pre-selected plan
          const preSelectedPlan = localStorage.getItem('selectedPlanFromLanding');
          if (preSelectedPlan) {
            const planInfo = JSON.parse(preSelectedPlan);
            const matchingPlan = paidPlans.find(plan => plan.id === planInfo.planId);
            if (matchingPlan) {
              setSelectedPlan(matchingPlan);
              setStep(2);
              localStorage.removeItem('selectedPlanFromLanding');
            } else {
              setStep(1);
            }
          } else {
            setStep(1);
          }
        } else {
          setError(data.error || 'Failed to load plans');
        }
      } catch (err) {
        setError('Failed to load subscription plans');
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  // Handle plan selection
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setStep(2);
  };

  const submitLabel = isFreeTrial ? 'Start My Free Trial' : 'Create Account & Pay';
  const submitLoadingLabel = isFreeTrial ? 'Creating your account...' : 'Creating Account & Redirecting to Payment...';

  const handleStartFreeTrial = () => {
    setIsFreeTrial(true);
    setSelectedPlan(null);
    setStep(2);
  };

  // Handle user registration
  const handleRegister = async (values) => {
    setLoading(true);
    setError('');
    
    try {
      const registerResponse = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          password: values.password,
          role: 'Admin Business Owner',
          plan_id: isFreeTrial ? undefined : (selectedPlan?.id || undefined),
        }),
      });

      const registerData = await registerResponse.json();

      if (!registerResponse.ok) {
        throw new Error(registerData.error || 'Registration failed');
      }

      // FREE TRIAL: log user in directly and redirect to dashboard
      if (registerData.freeTrial) {
        localStorage.setItem('token', registerData.token);
        localStorage.setItem('userId', registerData.userId);
        localStorage.setItem('userEmail', registerData.email);
        localStorage.setItem('userName', registerData.name);
        localStorage.setItem('userRole', registerData.role);
        window.location.href = '/admin/dashboard';
        return;
      }

      // PAID PLAN: redirect to Stripe checkout
      const { signupToken, planId, priceId } = registerData;

      const checkoutResponse = await fetch(`${API_BASE}/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signupToken,
          priceId: priceId || selectedPlan.stripe_price_id,
          planId: planId || selectedPlan.id,
        }),
      });

      const checkoutData = await checkoutResponse.json();

      if (!checkoutResponse.ok) {
        throw new Error(checkoutData.error || 'Payment setup failed');
      }

      window.location.href = checkoutData.url;
      
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const renderPlanSelection = () => (
    <div className="auth-wrapper">
      <div className="auth-card" style={{ maxWidth: '1200px', width: '95%' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            marginBottom: '24px',
            cursor: 'pointer',
            flexWrap: 'nowrap'
          }}
          onClick={() => window.location.href = '/'}
        >
          <img src="/headerlogo.png" alt="Asuma Logo" style={{ height: '48px', width: 'auto' }} />
          <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '700', whiteSpace: 'nowrap' }}>Choose Your Plan</h1>
        </div>
        <p style={{ textAlign: 'center', marginBottom: '30px', color: '#666' }}>
          Select the perfect plan for your needs.
        </p>

        {loadingPlans ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`plan-card ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
                hoverable
                onClick={() => handlePlanSelect(plan)}
                style={{
                  border: selectedPlan?.id === plan.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: '8px',
                  padding: '20px',
                  cursor: 'pointer'
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <Title level={4}>{plan.plan_name}</Title>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff' }}>
                    {Number(plan.monthly_price || 0) === 0
                      ? 'Free'
                      : (formatLocal(plan.monthly_price) || `£${plan.monthly_price}`)}
                    {Number(plan.monthly_price || 0) === 0 ? null : (
                      <span style={{ fontSize: '14px', color: '#666' }}>/month</span>
                    )}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  {plan.features && typeof plan.features === 'object' ? (
                    Object.entries(plan.features).map(([key, value]) => (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        <CheckOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                        <span style={{ fontSize: '14px' }}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {value}
                        </span>
                      </div>
                    ))
                  ) : (
                    <Text type="secondary">No features specified</Text>
                  )}
                </div>

                <Button type="primary" block size="large">
                  Select {plan.plan_name}
                </Button>
              </Card>
            ))}
          </div>
        )}
        
        <div style={{ textAlign: 'center', marginTop: '24px', padding: '20px', background: '#f0f7ff', borderRadius: '8px', border: '1px dashed #91caff' }}>
          <Text style={{ color: '#555', display: 'block', marginBottom: '8px' }}>
            Not ready to commit? Try Asuma free for 14 days — no credit card required.
          </Text>
          <Button type="link" style={{ fontWeight: '600', fontSize: '15px', padding: 0 }} onClick={handleStartFreeTrial}>
            Start Free Trial →
          </Button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <a href="/login">Already have an account? Log in</a>
        </div>
      </div>
    </div>
  );

  const renderRegistration = () => (
    <div className="auth-wrapper">
      <div className="auth-card">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            marginBottom: '24px',
            cursor: 'pointer',
            flexWrap: 'nowrap'
          }}
          onClick={() => window.location.href = '/'}
        >
          <img src="/headerlogo.png" alt="Asuma Logo" style={{ height: '48px', width: 'auto' }} />
          <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '700', whiteSpace: 'nowrap' }}>Create Your Account</h1>
        </div>
        <p style={{ textAlign: 'center', marginBottom: '20px', color: '#666' }}>
          {isFreeTrial ? (
            <span>🎉 <strong>14-day free trial</strong> — no credit card required</span>
          ) : selectedPlan ? (
            <>
              Selected plan: <strong>{selectedPlan?.plan_name}</strong>{' '}
              <span>- {formatLocal(selectedPlan?.monthly_price) || `£${selectedPlan?.monthly_price}`}/month</span>
            </>
          ) : (
            'No plan selected.'
          )}
        </p>

        {error && (
          <div className="auth-error" role="alert" style={{ marginBottom: '20px' }}>{error}</div>
        )}

        {!isFreeTrial && (
          <Alert
            message="Next Step: Payment"
            description="After creating your account, you will be redirected to Stripe to complete payment."
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}
        {isFreeTrial && (
          <Alert
            message="Free Trial — 14 days, 4,000 events"
            description="Your account will be created instantly. No payment needed. Trial ends after 14 days or 4,000 tracked events, whichever comes first."
            type="success"
            showIcon
            style={{ marginBottom: 20 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleRegister}
          size="large"
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter your full name' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="john@example.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter your password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="••••••••" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="••••••••" />
          </Form.Item>

          <Form.Item
            name="agreeChecked"
            valuePropName="checked"
            rules={[{ required: true, message: 'Please agree to the terms and conditions' }]}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" style={{ margin: 0, flexShrink: 0, width: '16px', height: '16px' }} />
              <span>
                I agree to the{' '}
                <a href="/terms-and-conditions.pdf" target="_blank" rel="noreferrer">Terms & Conditions</a>{' '}
                and{' '}
                <a href="/privacy-policy.pdf" target="_blank" rel="noreferrer">Privacy Policy</a>.
              </span>
            </label>
          </Form.Item>

          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              {loading ? submitLoadingLabel : submitLabel}
            </Button>
            
            <Button 
              type="default" 
              onClick={() => { setStep(1); setIsFreeTrial(false); }} 
              block 
              size="large"
              disabled={loading}
            >
              Back to Plans
            </Button>
            
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <a href="/login" style={{ color: '#1890ff' }}>
                Already have an account? Log in
              </a>
              <a href="/" style={{ color: '#1890ff' }}>
                Return to Home Page
              </a>
            </div>
          </Space>
        </Form>
      </div>
    </div>
  );

  return (
    <div className="signup-page">
      {step === 1 && renderPlanSelection()}
      {step === 2 && renderRegistration()}
    </div>
  );
};

export default Signup;
